import { memo, useMemo } from 'react';
import styled, { type CSSObject } from '@emotion/styled';

import type { ThemeProps } from '@emotion/react';

import bp from '../lib/cssMediaQuery';
import useDateDisplayWithFormat from '../lib/useDateDisplayWithFormat';
import useThemeStyles from '../lib/useThemeStyles';
import RSVPButton from './RSVPButton';
import {
  Maybe,
  Wws_WeddingWebsiteV2,
  type Wws_CoverPhotoItem,
  type Wws_Page,
  type Wws_PageItem,
} from '../types/graphql';
import CouplesImage from './CouplesImage';
interface Props {
  renderingPreviewImage: boolean;
  weddingWebsiteResult: Maybe<Wws_WeddingWebsiteV2> | undefined;
}

type DateFlourishImgProps = {
  countdown: boolean;
};

const defaultColors = {
  background: '#ffffff',
  nav: '#414042',
};

const DetailPane = styled('div')`
  display: flex;
  flex-direction: justify;
  align-items: center;
  position: relative;
  flex: 1;
  min-height: calc(100vh - 8rem);
  min-width: 420px;

  ${bp['max768']} {
    width: 100%;
    min-height: auto;
    min-width: 250px;
  }

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.DetailPane}
`;

const Card = styled('div')`
  background: #fff;
  width: 500px;
  min-height: 530px;
  display: flex;
  align-items: center;
  flex-direction: row;
  padding: 3rem 1rem;
  margin: 0 auto;

  ${bp['max768']} {
    justify-content: center;
    min-height: auto;
    width: 400px;
  }

  ${bp['max423']} {
    width: auto;
    min-height: 23.75rem;
  }

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.Card}
`;

const CardContent = styled('div')`
  width: 100%;

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.CardContent}
`;

const InitialsFlourishImg = styled('img')`
  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.InitialsFlourish}
`;

const NameFlourishImg = styled('img')`
  width: 100%;
  object-fit: contain;
  min-height: 200px;

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.NameFlourish}
`;

const DateFlourishImg = styled('img')<DateFlourishImgProps>`
  width: 100%;
  object-fit: contain;
  margin-top: 3rem;

  height: ${(props) => (props.countdown ? '125px' : '70px')};

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.DateFlourish}

  ${({ theme, countdown }): CSSObject | undefined =>
    countdown
      ? theme.liteSite?.CardLayout?.components?.CountdownFlourish
      : undefined}
`;

const Separator = styled('div')`
  width: 40%;
  border: 1px solid #414042;
  height: 0;
  margin: 3rem auto;

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.Separator}
`;

const RSVPButtonWrap = styled('span')`
  a {
    background: ${defaultColors.nav};
    padding: 0.75rem 1.25rem;
    line-height: 1;
    color: ${defaultColors.background};
    border: 1px solid ${defaultColors.nav};
    border-radius: 0;
    font-size: 0.875rem;

    ${bp['max768']} {
      margin: 0 auto;
    }

    ${({ theme }: ThemeProps): CSSObject | undefined =>
      theme.liteSite?.components?.Button}

    ${({ theme }: ThemeProps): CSSObject | undefined =>
      theme.liteSite?.CardLayout?.components?.Button}

    &:hover, &:focus {
      ${({ theme }: ThemeProps): CSSObject | undefined =>
        theme.liteSite?.components?.ButtonHover}
      ${({ theme }: ThemeProps): CSSObject | undefined =>
        theme.liteSite?.CardLayout?.components?.ButtonHover}
    }
  }
`;

function LiteSitePage({ renderingPreviewImage, weddingWebsiteResult }: Props) {
  const themeStyles = useThemeStyles();

  const date: string = useDateDisplayWithFormat() || '';

  const liteSitePage = weddingWebsiteResult?.pages?.find<Wws_Page>(
    (page: Wws_Page): page is Wws_Page => page.type === 'LiteSitePage'
  );

  const countdownDays = weddingWebsiteResult?.countdownDays;
  const days = countdownDays === 1 ? 'Day' : 'Days';

  const countdown =
    countdownDays !== null
      ? `${countdownDays} ${days} To Go!`
      : '';

  const coverPhotoItem = useMemo(
    () =>
      liteSitePage?.items.find(
        (value: Wws_PageItem): value is Wws_CoverPhotoItem => {
          return value.type === 'CoverPhotoItem';
        }
      ),
    [liteSitePage?.items]
  );

  let coverPhotoUrl = '';

  let focalPoint: ReadonlyArray<string> | null | undefined = [];

  if (coverPhotoItem) {
    coverPhotoUrl = coverPhotoItem.mediaUrl;
    focalPoint = coverPhotoItem.focalPoint;
  }

  const xFocalPoint =
    Array.isArray(focalPoint) && focalPoint[0] ? parseFloat(focalPoint[0]) : 0;

  const yFocalPoint =
    Array.isArray(focalPoint) && focalPoint[1] ? parseFloat(focalPoint[1]) : 0;

  // so that date/location will not be "null"
  const displayWeddingLocation: string =
    weddingWebsiteResult?.weddingLocation || '';

  if (
    weddingWebsiteResult &&
    typeof weddingWebsiteResult.theme !== 'undefined' &&
    weddingWebsiteResult.theme !== null &&
    weddingWebsiteResult.theme.id &&
    weddingWebsiteResult.firstName &&
    weddingWebsiteResult.fianceFirstName
  ) {
    const src1 = `${process.env['FLOURISH_DOMAIN']}/lockup/initials?firstName=${weddingWebsiteResult.firstName}&fianceFirstName=${weddingWebsiteResult.fianceFirstName}&themeId=${weddingWebsiteResult.theme.id}&liteSite=true`;
    const src2 = `${process.env['FLOURISH_DOMAIN']}/lockup/name?firstName=${weddingWebsiteResult.firstName}&fianceFirstName=${weddingWebsiteResult.fianceFirstName}&themeId=${weddingWebsiteResult.theme.id}&liteSite=true`;
    const src3 = `${process.env['FLOURISH_DOMAIN']}/lockup/dateLocation?date=${date}&location=${displayWeddingLocation}&countdown=${countdown}&themeId=${weddingWebsiteResult.theme.id}&liteSite=true&skipFormatting=true`;

    // Flourishes are contained in h1/h2 for SEO purposes.
    return (
      <>
        <CouplesImage
          renderingPreviewImage={renderingPreviewImage}
          xFocalPoint={xFocalPoint}
          yFocalPoint={yFocalPoint}
          coverPhotoUrl={coverPhotoUrl}
        />
        <DetailPane>
          <Card id="card">
            <CardContent id="card-content">
              <h1>
                {themeStyles.liteSite?.config?.displayInitialsLockup ? (
                  <InitialsFlourishImg
                    src={src1}
                    alt={`${weddingWebsiteResult.firstName || ''} & ${
                      weddingWebsiteResult.fianceFirstName
                    }`}
                  />
                ) : null}

                <NameFlourishImg
                  src={src2}
                  alt={`${weddingWebsiteResult.firstName || ''} & ${
                    weddingWebsiteResult.fianceFirstName
                  }`}
                />
              </h1>

              {date || displayWeddingLocation ? (
                <h2>
                  <DateFlourishImg
                    src={src3}
                    alt={`${date} ${countdown} ${displayWeddingLocation || ''}`}
                    countdown={Boolean(countdown)}
                  />
                </h2>
              ) : null}

              <Separator />

              <RSVPButtonWrap>
                <RSVPButton />
              </RSVPButtonWrap>
            </CardContent>
          </Card>
        </DetailPane>
      </>
    );
  }

  return <></>;
}

export default memo(LiteSitePage);
